/**
 * Custom Evaluator Builder Dialog.
 *
 * Two-step flow:
 *   1. Inputs   — collect description + optional grounding params, call
 *                 POST /generate-criteria for a preview rubric.
 *   2. Reviewing — show editable name/description/rubric + readonly chips,
 *                  save via POST /custom-evaluator-sets (initial_version) so
 *                  the user's edits are preserved verbatim. Optionally
 *                  publish the new draft to make it the active version.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import {
  useCreateCustomEvalSet,
  useGenerateCriteriaPreview,
  usePublishCustomEvalVersion,
} from '@/hooks/useCustomEvals';
import type { EvaluatorBuilderResponse } from '@/lib/api/customEvals';

// ─── Limits (kept in lockstep with backend schemas.py) ───────────────────────

const DESCRIPTION_MIN = 10;
const DESCRIPTION_SOFT_MAX = 4000;
const NAME_MAX = 255;
const GOAL_MAX = 1000;

// ─── Types ───────────────────────────────────────────────────────────────────

type Step = 'inputs' | 'reviewing';
type SaveStatus = 'draft' | 'active';

interface InputsForm {
  description: string;
  name: string;
  optimization_goal: string;
  desired_outcome: string;
  target_audience: string;
  use_training_context: boolean;
}

interface ReviewForm {
  name: string;
  description: string;
  rubric: string;
  status: SaveStatus | '';
}

const initialInputs: InputsForm = {
  description: '',
  name: '',
  optimization_goal: '',
  desired_outcome: '',
  target_audience: '',
  use_training_context: true,
};

// Match the slug rules used by PerformancePage's existing CreateRubricDialog so
// keys generated through either UI follow the same shape.
function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 128);
}

interface CustomEvalBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  /** When opened from interviewer detail, pin generation to that workspace. */
  interviewerId?: string;
  /** Fired after a successful save. */
  onCreated?: (setId: string) => void;
}

export function CustomEvalBuilderDialog({
  open,
  onOpenChange,
  organizationId,
  interviewerId,
  onCreated,
}: CustomEvalBuilderDialogProps) {
  const [step, setStep] = useState<Step>('inputs');
  const [inputs, setInputs] = useState<InputsForm>(initialInputs);
  const [inputErrors, setInputErrors] = useState<Partial<Record<keyof InputsForm, string>>>({});

  const [preview, setPreview] = useState<EvaluatorBuilderResponse | null>(null);
  const [review, setReview] = useState<ReviewForm>({ name: '', description: '', rubric: '', status: '' });
  const [reviewErrors, setReviewErrors] = useState<Partial<Record<keyof ReviewForm, string>>>({});

  const [confirmRegenerateOpen, setConfirmRegenerateOpen] = useState(false);

  // When status === "active", the save flow is two steps (create → publish).
  // If publish fails after a successful create, we persist the new IDs here
  // so the user can retry activation without re-running create (which would
  // 409 on the duplicate name and tempt them into renaming, which would
  // orphan the original draft).
  const [createdSet, setCreatedSet] = useState<{
    setId: string;
    versionId: string;
  } | null>(null);

  const previewMutation = useGenerateCriteriaPreview();
  const createMutation = useCreateCustomEvalSet();
  const publishMutation = usePublishCustomEvalVersion();

  const isBusy =
    previewMutation.isPending || createMutation.isPending || publishMutation.isPending;
  // The user has saved the eval but publish failed — name/description/rubric
  // edits would no longer round-trip (a retry skips the create call), so we
  // freeze those inputs and surface only the activate retry path.
  const isPostCreate = createdSet !== null;

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setStep('inputs');
      setInputs(initialInputs);
      setInputErrors({});
      setPreview(null);
      setReview({ name: '', description: '', rubric: '', status: '' });
      setReviewErrors({});
      setConfirmRegenerateOpen(false);
      setCreatedSet(null);
      previewMutation.reset();
      createMutation.reset();
      publishMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    // Block close-while-busy so we don't drop an in-flight save / publish.
    if (!next && isBusy) return;
    onOpenChange(next);
  };

  // ─── Step 1 helpers ────────────────────────────────────────────────────────

  const setInput = <K extends keyof InputsForm>(key: K, value: InputsForm[K]) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
    if (inputErrors[key]) {
      setInputErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validateInputs = (form: InputsForm): boolean => {
    const errors: Partial<Record<keyof InputsForm, string>> = {};
    const description = form.description.trim();
    if (description.length < DESCRIPTION_MIN) {
      errors.description = `Description must be at least ${DESCRIPTION_MIN} characters.`;
    } else if (description.length > DESCRIPTION_SOFT_MAX) {
      errors.description = `Description must be ${DESCRIPTION_SOFT_MAX} characters or fewer.`;
    }
    if (form.name.length > NAME_MAX) {
      errors.name = `Name must be ${NAME_MAX} characters or fewer.`;
    }
    if (form.optimization_goal.length > GOAL_MAX) {
      errors.optimization_goal = `Must be ${GOAL_MAX} characters or fewer.`;
    }
    if (form.desired_outcome.length > GOAL_MAX) {
      errors.desired_outcome = `Must be ${GOAL_MAX} characters or fewer.`;
    }
    if (form.target_audience.length > GOAL_MAX) {
      errors.target_audience = `Must be ${GOAL_MAX} characters or fewer.`;
    }
    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const runPreview = async (form: InputsForm) => {
    try {
      const result = await previewMutation.mutateAsync({
        organization_id: organizationId,
        description: form.description.trim(),
        name_hint: form.name.trim() || undefined,
        use_training_context: form.use_training_context,
        interviewer_id: interviewerId,
        optimization_goal: form.optimization_goal.trim() || undefined,
        desired_outcome: form.desired_outcome.trim() || undefined,
        target_audience: form.target_audience.trim() || undefined,
      });
      setPreview(result);
      setReview({
        name: result.suggested_name || form.name.trim(),
        description: result.suggested_description || '',
        rubric: result.rubric || '',
        status: 'draft',
      });
      setReviewErrors({});
      setStep('reviewing');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast({
        title: 'Could not draft criteria',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateInputs(inputs)) return;
    void runPreview(inputs);
  };

  // ─── Step 2 helpers ────────────────────────────────────────────────────────

  const setReviewField = <K extends keyof ReviewForm>(key: K, value: ReviewForm[K]) => {
    setReview((prev) => ({ ...prev, [key]: value }));
    if (reviewErrors[key]) {
      setReviewErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validateReview = (form: ReviewForm): boolean => {
    const errors: Partial<Record<keyof ReviewForm, string>> = {};
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      errors.name = 'Name is required.';
    } else if (trimmedName.length > NAME_MAX) {
      errors.name = `Name must be ${NAME_MAX} characters or fewer.`;
    } else if (!slugify(trimmedName)) {
      errors.name = 'Name must contain at least one alphanumeric character.';
    }
    if (!form.rubric.trim()) {
      errors.rubric = 'Scoring instructions cannot be empty.';
    }
    if (!form.status) {
      errors.status = 'Please select a status.';
    }
    setReviewErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preview) return;
    if (!validateReview(review)) return;

    const trimmedName = review.name.trim();
    const slug = slugify(trimmedName) || preview.suggested_slug;
    const trimmedDescription = review.description.trim();
    const trimmedRubric = review.rubric.trim();

    try {
      // Step 1: persist the set, unless a previous attempt already did. This
      // protects against the retry case where the create call succeeded but
      // the publish step failed — running create a second time would 409 on
      // the duplicate name and surface a misleading error.
      let setId: string;
      let versionId: string | null;

      if (createdSet) {
        setId = createdSet.setId;
        versionId = createdSet.versionId;
      } else {
        // TODO(backend-provenance): Client-built provenance below. If
        // knowledge_profile_id / training_snapshot_version pointers are needed
        // for authoritative grounding, backend must either return
        // generation_context from /generate-criteria or add a
        // persist-reviewed-preview endpoint that accepts the edited rubric
        // verbatim.
        const created = await createMutation.mutateAsync({
          organization_id: organizationId,
          name: trimmedName,
          description: trimmedDescription || undefined,
          initial_version: {
            notes: 'Created via AI builder (preview-edited)',
            criteria: [
              {
                key: slug,
                name: trimmedName,
                rubric: trimmedRubric,
                score_type: 'integer_0_100',
                weight: 1.0,
                order_index: 0,
                is_required: true,
                metadata: {
                  generated_by: 'evaluator_builder_agent',
                  source: 'preview_edited',
                  user_description: inputs.description.trim(),
                  optimization_goal: inputs.optimization_goal.trim() || null,
                  desired_outcome: inputs.desired_outcome.trim() || null,
                  target_audience: inputs.target_audience.trim() || null,
                  target_interviewer_id: interviewerId ?? null,
                  use_training_context: inputs.use_training_context,
                  key_checkpoints: preview.key_checkpoints,
                  diagnostic_tags: preview.diagnostic_tags,
                },
              },
            ],
          },
        });

        const draftVersion = created.versions?.[0];
        if (!draftVersion) {
          // The set was persisted but we can't find the new version — surface
          // a partial-success notice and bail to the list, where the user can
          // publish from the card.
          toast({
            title: 'Eval saved as draft',
            description: 'Could not locate the new version to publish. Publish it from the list.',
          });
          onCreated?.(created.id);
          onOpenChange(false);
          return;
        }

        setId = created.id;
        versionId = draftVersion.id;
        setCreatedSet({ setId, versionId });
      }

      // Step 2: publish iff the user picked "active".
      if (review.status === 'active') {
        try {
          await publishMutation.mutateAsync({
            setId,
            versionId: versionId!,
            organizationId,
          });
        } catch (publishError) {
          const message =
            publishError instanceof Error ? publishError.message : 'Unknown error';
          toast({
            title: 'Eval saved as draft',
            description: `Failed to activate — try Save & activate again, or publish from the list. (${message})`,
          });
          // Dialog stays open. createdSet is set, so a retry click will skip
          // the create step and only re-attempt publish.
          return;
        }
      }

      toast({
        title: 'Evaluator created',
        description: review.status === 'active'
          ? `"${trimmedName}" is now active.`
          : `"${trimmedName}" saved as draft.`,
      });
      onCreated?.(setId);
      onOpenChange(false);
    } catch (error) {
      // Only fires when create itself throws — publish errors are caught
      // inside their own try/catch above.
      const message = error instanceof Error ? error.message : 'Unknown error';
      const isDuplicate = /duplicate key|already exists|unique constraint/i.test(message);
      if (isDuplicate) {
        setReviewErrors((prev) => ({
          ...prev,
          name: 'An evaluator with this name already exists.',
        }));
      } else {
        toast({
          title: 'Could not save evaluator',
          description: message,
          variant: 'destructive',
        });
      }
    }
  };

  const handleRequestRegenerate = () => {
    if (isBusy) return;
    setConfirmRegenerateOpen(true);
  };

  const handleConfirmRegenerate = async () => {
    setConfirmRegenerateOpen(false);
    await runPreview(inputs);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {step === 'inputs' ? 'Create evaluator' : 'Review evaluator'}
            </DialogTitle>
            <DialogDescription>
              {step === 'inputs'
                ? 'Describe what you want to score. Hobbes will draft criteria you can edit before saving.'
                : 'Review and edit the generated criteria. Saving captures your edits exactly.'}
            </DialogDescription>
          </DialogHeader>

          {step === 'inputs' ? (
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="cee-description">
                  What should this evaluator measure?
                </Label>
                <Textarea
                  id="cee-description"
                  rows={4}
                  placeholder="e.g. Does the agent surface the prospect's success metric and tie it back to ROI before pitching the product?"
                  value={inputs.description}
                  onChange={(e) => setInput('description', e.target.value)}
                  disabled={isBusy}
                  maxLength={DESCRIPTION_SOFT_MAX}
                />
                {inputErrors.description && (
                  <p className="text-xs text-destructive">{inputErrors.description}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cee-name">Name (optional)</Label>
                <Input
                  id="cee-name"
                  placeholder="ROI Discovery"
                  value={inputs.name}
                  onChange={(e) => setInput('name', e.target.value)}
                  disabled={isBusy}
                  maxLength={NAME_MAX}
                />
                {inputErrors.name && (
                  <p className="text-xs text-destructive">{inputErrors.name}</p>
                )}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cee-goal">Optimization goal (optional)</Label>
                  <Textarea
                    id="cee-goal"
                    rows={2}
                    placeholder="What customer outcome should this drive?"
                    value={inputs.optimization_goal}
                    onChange={(e) => setInput('optimization_goal', e.target.value)}
                    disabled={isBusy}
                    maxLength={GOAL_MAX}
                  />
                  {inputErrors.optimization_goal && (
                    <p className="text-xs text-destructive">{inputErrors.optimization_goal}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cee-outcome">Desired outcome (optional)</Label>
                  <Textarea
                    id="cee-outcome"
                    rows={2}
                    placeholder="What should the visitor leave with?"
                    value={inputs.desired_outcome}
                    onChange={(e) => setInput('desired_outcome', e.target.value)}
                    disabled={isBusy}
                    maxLength={GOAL_MAX}
                  />
                  {inputErrors.desired_outcome && (
                    <p className="text-xs text-destructive">{inputErrors.desired_outcome}</p>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cee-audience">Target audience (optional)</Label>
                <Input
                  id="cee-audience"
                  placeholder="e.g. RevOps leaders at mid-market SaaS"
                  value={inputs.target_audience}
                  onChange={(e) => setInput('target_audience', e.target.value)}
                  disabled={isBusy}
                  maxLength={GOAL_MAX}
                />
                {inputErrors.target_audience && (
                  <p className="text-xs text-destructive">{inputErrors.target_audience}</p>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/20 px-4 py-3">
                <Label htmlFor="cee-grounding" className="inline-flex items-center text-sm">
                  Use Agent Knowledge
                  <HelpTooltip text="Use Agent Knowledge to ground the suggested criteria." />
                </Label>
                <Switch
                  id="cee-grounding"
                  checked={inputs.use_training_context}
                  onCheckedChange={(checked) => setInput('use_training_context', checked)}
                  disabled={isBusy}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isBusy}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isBusy}>
                  {previewMutation.isPending ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Drafting criteria
                    </span>
                  ) : (
                    'Draft criteria'
                  )}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <form onSubmit={handleSave} className="space-y-4">
              {isPostCreate && (
                <Alert>
                  <AlertDescription>
                    Saved as draft. Activation didn&rsquo;t go through — try
                    Save &amp; activate again, or close this dialog and use the
                    Activate button on the card.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="cee-rev-name">Name</Label>
                <Input
                  id="cee-rev-name"
                  value={review.name}
                  onChange={(e) => setReviewField('name', e.target.value)}
                  disabled={isBusy || isPostCreate}
                  maxLength={NAME_MAX}
                />
                {reviewErrors.name && (
                  <p className="text-xs text-destructive">{reviewErrors.name}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cee-rev-description">Description</Label>
                <Textarea
                  id="cee-rev-description"
                  rows={2}
                  value={review.description}
                  onChange={(e) => setReviewField('description', e.target.value)}
                  disabled={isBusy || isPostCreate}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cee-rev-rubric">Scoring instructions</Label>
                <Textarea
                  id="cee-rev-rubric"
                  rows={14}
                  className="font-mono text-xs"
                  value={review.rubric}
                  onChange={(e) => setReviewField('rubric', e.target.value)}
                  disabled={isBusy || isPostCreate}
                />
                {reviewErrors.rubric && (
                  <p className="text-xs text-destructive">{reviewErrors.rubric}</p>
                )}
              </div>

              {preview && preview.key_checkpoints.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Key checkpoints
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.key_checkpoints.map((checkpoint) => (
                      <Badge key={checkpoint} variant="secondary" className="text-[11px]">
                        {checkpoint}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {preview && preview.diagnostic_tags.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Diagnostic tags
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.diagnostic_tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-[11px]">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select
                  value={review.status}
                  onValueChange={(v) => setReviewField('status', v as SaveStatus)}
                  disabled={isBusy}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                  </SelectContent>
                </Select>
                {reviewErrors.status && (
                  <p className="text-xs text-destructive">{reviewErrors.status}</p>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2 sm:justify-between">
                <div className="flex flex-wrap gap-2">
                  {/* Back / Regenerate are only meaningful before the set is
                      persisted. Once create succeeds the set's identity is
                      pinned, so re-running the preview or going back to the
                      inputs step would silently disconnect from what's saved. */}
                  {!isPostCreate && (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setStep('inputs')}
                        disabled={isBusy}
                      >
                        Back
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRequestRegenerate}
                        disabled={isBusy}
                      >
                        Regenerate
                      </Button>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isBusy}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isBusy}>
                    {createMutation.isPending || publishMutation.isPending ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving&hellip;
                      </span>
                    ) : (
                      'Save evaluator'
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmRegenerateOpen}
        onOpenChange={(next) => {
          if (!isBusy) setConfirmRegenerateOpen(next);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard your edits and regenerate?</AlertDialogTitle>
            <AlertDialogDescription>
              The current scoring instructions, name, and description will be replaced by a fresh
              generation. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRegenerate}>
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default CustomEvalBuilderDialog;
